import { usePublishedData } from "../lib/published-data";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { DocumentContent } from "../components/document-content";
import { documentHead, useShell } from "../lib/document-route";
import { guideHead } from "../components/matchups/guide-head";
import { GuidePage } from "../components/matchups/guide-page";
import { keepSearch, searchText } from "../lib/search";
import { loadMatchupGuide } from "../server/matchups";

/**
 * A matchup guide's overview. Each visit asks for it afresh and nothing keeps it once the visitor
 * leaves; switching between its Simple, Detailed and Both views asks for nothing.
 */
export const Route = createFileRoute("/matchups/$slug/")({
  validateSearch: keepSearch,
  loader: async ({ params }) => {
    const guide = await loadMatchupGuide({ data: { slug: params.slug } });
    if (!guide) throw notFound();
    if (guide.kind === "redirect") throw redirect({ href: guide.to, statusCode: 302 });
    return guide;
  },
  staleTime: 0,
  gcTime: 0,
  // Switching views stays on the loaded guide. Entering one follows the router's freshness rules:
  // a visitor without an account uses the guide they just prefetched on hover; anyone else (who
  // never prefetches, with nothing kept) loads it afresh.
  shouldReload: ({ cause }) => (cause === "stay" ? false : undefined),
  head: ({ matches, loaderData, match }) =>
    loaderData?.kind === "page"
      ? documentHead(matches, loaderData, match.pathname, {
          title: (shell) => shell.creator.name,
          description: () => "",
        })
      : guideHead(matches, loaderData),
  component: Overview,
});

function Overview() {
  const initial = Route.useLoaderData();
  const requested = searchText(Route.useSearch(), "view");
  const view = requested === "detailed" || requested === "both" ? requested : "simple";
  const shell = useShell();
  const { data: guide } = usePublishedData(
    initial,
    shell,
    `/matchups/${Route.useParams().slug}`,
    "page",
  );
  if (guide.kind === "page") return <DocumentContent data={guide} shell={shell} />;
  return <GuidePage guide={guide} view={view} />;
}
